'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { generateBlogKey } from '@eliadmin/core/src/utils/blog-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { BLOGS_CONFIG } from '@eliadmin/core/src/constants/blogs-config';
import React from 'react';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { HiArrowLongRight } from 'react-icons/hi2';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

const [BlogCategoriesProvider, useBlogCategories] = createContextStore<
  { categories: BlogCategory[]; activeCategoryIds: string[] },
  { setActiveCategories: (categoryIds: string[]) => void }
>(
  {
    categories: [],
    activeCategoryIds: [],
  },
  set => ({
    setActiveCategories: (categoryIds: string[]) => set({ activeCategoryIds: categoryIds }),
  }),
);

export { BlogCategoriesProvider, useBlogCategories };

export type BlogCardsProps = {
  locale: string;
  query: FetchBlogsQuery;
  className?: {
    blog?: {
      main?: string;
      link?: string;
      image?: string;
      title?: string;
      date?: string;
      contentContainer?: string;
      content?: string;
    };
  };
};

export const BlogCards = ({ locale, query: queryProp, className }: BlogCardsProps) => {
  const { t } = useTranslations();

  const searchParams = useSearchParams();

  const search = searchParams.get('search');
  const activeCategories = useBlogCategories(s => s.activeCategoryIds);

  const query = useMemo(
    () => ({
      where:
        !('filter' in queryProp) && activeCategories.length > 0
          ? { categories: { in: activeCategories } }
          : undefined,
      search: search ?? undefined,
      ...queryProp,
    }),
    [activeCategories, queryProp, search],
  );
  const queryKey = useMemo(
    () => ['blogs', locale, JSON.stringify(query)] as const,
    [locale, query],
  );

  const fetchBlogsFn = async ({ pageParam = 1 }) => {
    const { data } = await fetchBlogs({
      locale,
      query: { ...query, page: pageParam } as any,
    });
    return data;
  };

  const {
    data: blogs,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery({
    initialPageParam: 0,
    queryKey,
    queryFn: fetchBlogsFn,
    getNextPageParam: (lastPage, pages) => pages.length + 1,
  });

  return (
    <div className="flex w-full flex-col px-6">
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3',
          isFetching && 'opacity-30',
        )}
      >
        {blogs?.pages.map(p => {
          return p.map((blog, i) => {
            function formatBlogDate(dateString: string, locale: string) {
              const date = new Date(dateString);

              const day = date.getDate();
              const monthIndex = date.getMonth();
              const year = date.getFullYear();

              const months = [
                'Januára',
                'Februára',
                'Marca',
                'Apríla',
                'Mája',
                'Júna',
                'Júla',
                'Augusta',
                'Septembra',
                'Octóbra',
                'Novembra',
                'Decembra',
              ];

              // Get the month name based on the month index
              const month = months[monthIndex];

              return `${day}. ${month} ${year}`;
            }

            const textContent = getTextFromHTML(blog.content);

            if (i === 0 && query.search) return null;
            return (
              <Card
                key={generateBlogKey(blog)}
                className={cn(
                  'relative col-span-1 w-full rounded-none bg-[#12151B] md:max-w-[1000px]',
                  'ea-blog-card',
                  className?.blog?.main,
                )}
              >
                <Link
                  href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
                  className={cn('ea-blog-card__link w-full', className?.blog?.link)}
                  prefetch={false}
                >
                  <CardContent
                    className={cn(
                      'bg-[#10141b] p-0',
                      'ea-blog-card__content',
                      className?.blog?.contentContainer,
                    )}
                  >
                    <Image
                      alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
                      src={blog.image?.url || '/images/placeholder.png'}
                      width="0"
                      height="0"
                      sizes="100vw"
                      className={cn(
                        'h-full  w-full ',
                        'ea-blog-card__image',
                        className?.blog?.image,
                      )}
                    />
                    <div
                      className={cn(
                        'flex flex-col',
                        'ea-blog-card__content-container',
                        className?.blog?.contentContainer,
                      )}
                    >
                      {/* blog card created date */}
                      <div
                        className={cn(
                          'w-full pb-5 pt-10 text-xs font-bold tracking-[0.5em] text-[#12EAA6]',
                          'ea-blog-card__date',
                        )}
                      >
                        {formatBlogDate(blog.created_at, locale)}
                      </div>
                      {/* title + button */}
                      <div className="flex w-full flex-col items-start justify-start gap-5 md:flex-row md:items-center md:justify-between">
                        <h3
                          data-ea-visual-tag="h6"
                          className={cn(
                            'line-clamp-2 text-ellipsis text-2xl font-bold text-white sm:text-3xl lg:text-3xl',
                            'ea-blog-card__title',
                            className?.blog?.title,
                          )}
                        >
                          {blog.name}
                        </h3>
                        <Button
                          asChild
                          className="rounded-none bg-[#12EAA6] shadow-none hover:bg-[#12eaa6bc]"
                        >
                          <span data-ea-visual-tag="button" aria-hidden="true">
                            <HiArrowLongRight className={'h-full w-full text-[#516589]'} />
                          </span>
                        </Button>
                      </div>
                      <HtmlRendererRaw
                        as="p"
                        className={cn(
                          'h-13 mt-5 line-clamp-3 text-ellipsis text-sm leading-6 text-muted/75',
                          'ea-blog-card__content',
                        )}
                        html={textContent}
                      />
                    </div>
                  </CardContent>
                </Link>
              </Card>
            );
          });
        })}
      </div>
      {blogs?.pages.length === 1 && blogs?.pages?.every(page => page.length === 0) && (
        <div className="flex w-full items-center justify-center">
          <p className="text-foreground">{t('text554')}</p>
        </div>
      )}
      <div className="my-10 flex justify-center">
        {blogs?.pages.length > 0 && blogs?.pages[blogs?.pages.length - 1].length > 0 && (
          <Button
            onClick={() => fetchNextPage()}
            variant={'outline'}
            disabled={isFetching}
            className="rounded-none border-none bg-primary font-medium text-[#10141b] hover:bg-primary/90"
          >
            {t('text588')}
          </Button>
        )}
      </div>
    </div>
  );
};
